import React from 'react';
import { Button, Icon, ContentBlock, Tag } from '../../DesignSystem';
import {
  OrganizerSubscription,
  getPlanById,
  formatPriceWithInterval,
  getLaunchPriceInfo,
  isPro
} from '../../../types/subscription.types';
import './SubscriptionCard.css';

interface SubscriptionCardProps {
  subscription: OrganizerSubscription | null;
  onManageClick: () => void;
  loading?: boolean;
}

/**
 * Card displaying subscription plan info
 * Shows plan badge, usage (free) or renewal date (pro)
 */
const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
  subscription,
  onManageClick,
  loading = false
}) => {
  if (loading) {
    return (
      <ContentBlock className="subscription-card subscription-card--loading" border="light">
        <div className="subscription-card__skeleton">
          <div className="subscription-card__skeleton-badge" />
          <div className="subscription-card__skeleton-line" />
          <div className="subscription-card__skeleton-line subscription-card__skeleton-line--short" />
        </div>
      </ContentBlock>
    );
  }

  // Default to free plan if no subscription
  const plan = subscription ? getPlanById(subscription.planId) : getPlanById('free');
  const isProPlan = subscription ? isPro(subscription.planId) : false;

  // Format renewal date for Pro plans
  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Calculate events usage for free plan
  const eventsUsed = subscription?.eventsUsed ?? 0;
  const eventsLimit = subscription?.eventsLimit ?? 5;
  const usagePercentage = eventsLimit > 0 ? (eventsUsed / eventsLimit) * 100 : 0;

  return (
    <ContentBlock className="subscription-card" border="light">
      <div className="subscription-card__header">
        <Icon name="Crown" size={18} />
        <span>My Subscription</span>
      </div>

      <div className="subscription-card__content">
        {/* Plan Badge */}
        <div className="subscription-card__plan-row">
          <Tag
            label={plan.name}
            type="status"
            status={isProPlan ? 'published' : 'draft'}
            size="md"
          />
          <div className="subscription-card__price-wrapper">
            {getLaunchPriceInfo(plan).isLaunchPrice && (
              <>
                <Tag label="Launch Price" type="status" status="published" size="sm" />
                <span className="subscription-card__regular-price">
                  {getLaunchPriceInfo(plan).regularPriceFormatted}
                </span>
              </>
            )}
            <span className="subscription-card__price">
              {formatPriceWithInterval(plan)}
            </span>
          </div>
        </div>

        {/* Status/Usage Info */}
        <div className="subscription-card__info">
          {isProPlan ? (
            // Pro plan: show renewal date and status
            <>
              <div className="subscription-card__status">
                <Icon name="Check" size={14} />
                <span>Events unlimited</span>
              </div>
              {subscription && (
                <div className="subscription-card__renewal-row">
                  <div className="subscription-card__renewal">
                    <Icon name="Calendar" size={14} />
                    <span>
                      {subscription.cancelAtPeriodEnd
                        ? `Expires: ${formatDate(subscription.currentPeriodEnd)}`
                        : `Renewal: ${formatDate(subscription.currentPeriodEnd)}`}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onManageClick}
                    className="subscription-card__billing-history"
                  >
                    Billing history
                  </Button>
                </div>
              )}
            </>
          ) : (
            // Free plan: show usage
            <>
              <div className="subscription-card__usage">
                <div className="subscription-card__usage-label">
                  <span>{eventsUsed}/{eventsLimit} events used</span>
                  <span className="subscription-card__usage-remaining">
                    {eventsLimit - eventsUsed} remaining
                  </span>
                </div>
                <div className="subscription-card__usage-bar">
                  <div
                    className={`subscription-card__usage-fill ${
                      usagePercentage >= 80 ? 'subscription-card__usage-fill--warning' : ''
                    } ${
                      usagePercentage >= 100 ? 'subscription-card__usage-fill--danger' : ''
                    }`}
                    style={{ width: `${Math.min(100, usagePercentage)}%` }}
                  />
                </div>
              </div>
              {usagePercentage >= 80 && (
                <div className="subscription-card__upgrade-hint">
                  <Icon name="Zap" size={14} />
                  <span>Upgrade to Pro for unlimited events</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Action Button */}
        <div className="subscription-card__actions">
          <Button
            variant={isProPlan ? 'secondary' : 'primary'}
            size="sm"
            onClick={onManageClick}
            className="subscription-card__button"
          >
            {isProPlan ? 'Manage subscription' : 'Upgrade to Pro'}
          </Button>
        </div>
      </div>
    </ContentBlock>
  );
};

export default SubscriptionCard;
